// Helper function to get basename in browser environment
const getBasename = (filePath) => {
  return filePath.split(/[/\\]/).pop() || filePath;
};

// Helper function to get the containing directory in browser environment
const getDirname = (filePath) => {
  const idx = Math.max(filePath.lastIndexOf('/'), filePath.lastIndexOf('\\'));
  return idx > 0 ? filePath.slice(0, idx) : filePath;
};

// Resolve the output path for one export, shared by folder and cloud flows.
// Normal mode: <outputDirectory>/<name><suffix>.<format ext>, auto-numbered to
// never collide. Overwrite mode: the image's OWN folder + original basename,
// keeping the source extension when it matches the format family (photo.jpg
// stays photo.jpg), no auto-numbering — the export replaces the original.
// Iteration batches (>1 outputs per image) always keep suffix+numbering.
const resolveOutputPath = async (filePath, outputDirectory, settings, imageIterations, iter) => {
  const { format, prefix, overwriteOriginals } = settings;
  const originalName = getBasename(filePath).replace(/\.[^/.]+$/, '');
  const overwriteMode = !!overwriteOriginals && imageIterations === 1;

  let fileExtension = format === 'icon' ? 'svg' : format;
  if (overwriteMode) {
    const origExt = (getBasename(filePath).split('.').pop() || '').toLowerCase();
    const family = { jpeg: ['jpg', 'jpeg'], png: ['png'], webp: ['webp'], avif: ['avif'] }[format] || [format];
    if (family.includes(origExt)) fileExtension = origExt;
  }

  const suffix = overwriteMode ? '' : (prefix || '-pixl');
  const targetDir = overwriteMode ? getDirname(filePath) : outputDirectory;

  let fileName;
  if (imageIterations > 1) {
    fileName = `${originalName}${suffix}${String(iter + 1).padStart(2, '0')}.${fileExtension}`;
  } else {
    fileName = `${originalName}${suffix}.${fileExtension}`;
  }

  let outputPath = `${targetDir}/${fileName}`;
  if (!overwriteMode) {
    let fileCounter = 1;
    while (await window.electronAPI.fileExists(outputPath)) {
      if (imageIterations > 1) {
        fileName = `${originalName}${suffix}${String(iter + 1).padStart(2, '0')}_${fileCounter}.${fileExtension}`;
      } else {
        fileName = `${originalName}${suffix}${String(fileCounter).padStart(2, '0')}.${fileExtension}`;
      }
      outputPath = `${targetDir}/${fileName}`;
      fileCounter++;
    }
  }
  return { outputPath, fileName };
};

export const processImages = async (filePaths, outputDirectory, settings, onProgress, licenseInfo = null, isCancelled = null, iterations = 1) => {
  const { targetSize, shortEdgeSize, format, quality, prefix, enableUpscaling, cropWidth, cropHeight, enableAIFill } = settings;
  const results = [];
  
  try {
    // Calculate total iterations for progress tracking
    // Ensure iterations is a valid number
    const validIterations = iterations && !isNaN(iterations) && iterations >= 1 ? parseInt(iterations, 10) : 1;
    const actualIterations = enableAIFill && (cropWidth || cropHeight) ? validIterations : 1;
    const total = filePaths.length * actualIterations;
    let processedCount = 0;
    let upscaledCount = 0;
    
    for (let i = 0; i < filePaths.length; i++) {
      const filePath = filePaths[i];
      
      // Determine how many iterations for this image
      const imageIterations = enableAIFill && (cropWidth || cropHeight) ? validIterations : 1;
      
      for (let iter = 0; iter < imageIterations; iter++) {
        // Check if processing was cancelled
        if (isCancelled && isCancelled()) {
          throw new Error('Processing cancelled by user');
        }
        
        // Resolve output path (handles suffix, auto-numbering, overwrite mode)
        const { outputPath } = await resolveOutputPath(filePath, outputDirectory, settings, imageIterations, iter);

        // Update progress BEFORE processing starts
        // Calculate progress based on current item being processed, not completed count
        const currentItem = processedCount + 1;
        
        // For small batches (1-3 images), show more granular progress
        let baseProgress, progressDuringProcessing;
        if (total <= 3) {
          // For 1-3 images, show intermediate steps: loading (20%), processing (50%), saving (80%)
          const itemProgress = (currentItem - 1) / total;
          baseProgress = itemProgress * 100 + (1 / total * 20); // Add 20% for "loading" phase
          progressDuringProcessing = itemProgress * 100 + (1 / total * 50); // 50% through current image
        } else {
          // For larger batches, use traditional progress
          baseProgress = ((currentItem - 1) / total) * 100;
          progressDuringProcessing = (currentItem - 0.5) / total * 100;
        }
        
        let progressMessage = `Processing: ${currentItem}/${total}`;
        
        // Check if this image will use AI fill
        if (enableAIFill && (cropWidth || cropHeight)) {
          if (imageIterations > 1) {
            progressMessage = `Processing with AI Fill (iteration ${iter + 1}/${imageIterations}): ${currentItem}/${total}`;
          } else {
            progressMessage = `Processing with AI Fill: ${currentItem}/${total}`;
          }
        }
        
        // Show progress at the start of processing this item
        onProgress(baseProgress, progressMessage);
      
      // Check if we need to upscale
      let processPath = filePath;
      if (enableUpscaling) {
        console.log(`\n=== Checking upscaling for image ${i + 1} ===`);
        console.log(`File: ${filePath}`);
        
        // Get image metadata to check if upscaling is needed
        const metadata = await window.electronAPI.getImageMetadata(filePath);
        if (metadata.success) {
          const longEdge = Math.max(metadata.width, metadata.height);
          const shortEdge = Math.min(metadata.width, metadata.height);
          const edgeToCheck = shortEdgeSize ? shortEdge : longEdge;
          const targetValue = shortEdgeSize || targetSize;
          console.log(`Image ${getBasename(filePath)}: ${metadata.width}x${metadata.height}, long edge: ${longEdge}, short edge: ${shortEdge}, target: ${parseInt(targetValue)}`);
          
          if (edgeToCheck < parseInt(targetValue)) {
            console.log(`Image needs upscaling: ${edgeToCheck} < ${targetValue}`);
            // Image needs upscaling - show mid-point progress
            onProgress(progressDuringProcessing, `⬆️ UPSCALING image ${i + 1} from ${edgeToCheck}px to ${targetValue}px...`);
            
            const upscaleResult = await window.electronAPI.upscaleImage(filePath, licenseInfo, {
              targetSize: targetSize ? parseInt(targetSize) : undefined,
              shortEdgeSize: shortEdgeSize ? parseInt(shortEdgeSize) : undefined
            });
            console.log('Upscale result:', upscaleResult);
            
            if (upscaleResult.success) {
              // Use the upscaled image data directly in the next processing step
              // This will be handled in the electron process
              processPath = upscaleResult.imagePath || filePath;
              upscaledCount++;
              console.log(`Successfully upscaled to: ${processPath}`);
            } else {
              console.warn(`Failed to upscale ${filePath}:`, upscaleResult.error);
              
              // Check if it's a limit exceeded error
              if (upscaleResult.limitExceeded) {
                throw {
                  limitExceeded: true,
                  limitInfo: {
                    message: upscaleResult.error,
                    action: upscaleResult.action,
                    tier: upscaleResult.tier
                  }
                };
              }
              
              // Continue with original image if upscaling fails for other reasons
            }
          } else {
            console.log(`Image doesn't need upscaling: ${edgeToCheck} >= ${targetValue}`);
          }
        }
      }
      
      // Show "processing" phase for small batches
      if (total <= 3) {
        const processingProgress = (currentItem - 1) / total * 100 + (1 / total * 60); // 60% through current image
        onProgress(processingProgress, `Processing image ${currentItem}/${total}...`);
      }
      
      // Process the image (original or upscaled)
      const result = await window.electronAPI.processImage(processPath, outputPath, {
        targetSize: targetSize ? parseInt(targetSize) : undefined,
        shortEdgeSize: shortEdgeSize ? parseInt(shortEdgeSize) : undefined,
        cropWidth: cropWidth ? parseInt(cropWidth) : undefined,
        cropHeight: cropHeight ? parseInt(cropHeight) : undefined,
        enableAIFill: enableAIFill || false,
        cropAlignment: settings.cropAlignment || 'center',
        useSystemPrompt: settings.useSystemPrompt !== undefined ? settings.useSystemPrompt : true,
        customPrompt: settings.customPrompt || '',
        format,
        quality: parseInt(quality),
        licenseInfo
      });
      
      if (result.success) {
        // Show "saving" phase for small batches
        if (total <= 3) {
          const savingProgress = (currentItem - 1) / total * 100 + (1 / total * 80); // 80% through current image
          onProgress(savingProgress, `Saving image ${currentItem}/${total}...`);
        }
        
        // NOW increment the processed count after successful processing
        processedCount++;
        
        results.push({
          inputPath: filePath,
          outputPath,
          ...result
        });
      } else {
        console.error(`Failed to process ${filePath}:`, result.error);
        
        // Check if it's a limit exceeded error from AI Fill
        if (result.limitExceeded) {
          throw {
            limitExceeded: true,
            limitInfo: {
              message: result.error,
              action: result.action,
              tier: result.tier
            }
          };
        }
        
        throw new Error(`Failed to process ${getBasename(filePath)}: ${result.error}`);
      }
      } // End of iterations loop
    } // End of files loop
    
    // Final progress update with upscaling summary
    if (enableUpscaling && upscaledCount > 0) {
      onProgress(100, `Complete! ${upscaledCount} of ${filePaths.length} images were upscaled with AI`);
    } else if (enableUpscaling) {
      onProgress(100, `Complete! No images needed upscaling (all were already large enough)`);
    } else {
      onProgress(100, 'Complete!');
    }
    
    return results;
  } catch (error) {
    console.error('Error in processImages:', error);
    throw error;
  }
};

export const uploadToS3 = async (filePaths, outputDirectory, settings, awsCredentials, onProgress, licenseInfo = null, isCancelled = null, iterations = 1) => {
  const { targetSize, shortEdgeSize, format, quality, prefix, enableUpscaling, cropWidth, cropHeight, enableAIFill } = settings;
  const { bucketName, folderPath } = awsCredentials;
  
  try {
    // Calculate total iterations for progress tracking
    const validIterations = iterations && !isNaN(iterations) && iterations >= 1 ? parseInt(iterations, 10) : 1;
    const actualIterations = enableAIFill && (cropWidth || cropHeight) ? validIterations : 1;
    const total = filePaths.length * actualIterations;
    const processedFiles = [];
    const uploadedFiles = [];
    let processedCount = 0;
    let upscaledCount = 0;
    
    // Step 1: Process images (0-50% progress)
    for (let i = 0; i < filePaths.length; i++) {
      const filePath = filePaths[i];
      
      // Determine how many iterations for this image
      const imageIterations = enableAIFill && (cropWidth || cropHeight) ? validIterations : 1;
      
      for (let iter = 0; iter < imageIterations; iter++) {
        // Check if processing was cancelled
        if (isCancelled && isCancelled()) {
          throw new Error('Processing cancelled by user');
        }
        
        // Resolve output path (handles suffix, auto-numbering, overwrite mode)
        const { outputPath, fileName } = await resolveOutputPath(filePath, outputDirectory, settings, imageIterations, iter);

        // Update progress for processing (0-50%)
        const currentItem = processedCount + 1;
        
        // For small batches, show more granular progress
        let processProgress, progressDuringProcessing;
        if (total <= 3) {
          const itemProgress = (currentItem - 1) / total;
          processProgress = itemProgress * 50 + (1 / total * 10); // Add 10% for "loading" phase
          progressDuringProcessing = itemProgress * 50 + (1 / total * 25); // 25% through current image
        } else {
          processProgress = ((currentItem - 1) / total) * 50;
          progressDuringProcessing = ((currentItem - 0.5) / total) * 50;
        }
        
        let progressMessage = `Processing: ${currentItem}/${total}`;
        
        if (enableAIFill && (cropWidth || cropHeight) && imageIterations > 1) {
          progressMessage = `Processing with AI Fill (iteration ${iter + 1}/${imageIterations}): ${currentItem}/${total}`;
        } else if (upscaledCount > 0) {
          progressMessage = `Processing: ${currentItem}/${total} (${upscaledCount} upscaled)`;
        }
        
        onProgress(processProgress, progressMessage);
      
        // Check if we need to upscale
        let processPath = filePath;
        if (enableUpscaling) {
          // Get image metadata to check if upscaling is needed
          const metadata = await window.electronAPI.getImageMetadata(filePath);
          if (metadata.success) {
            const longEdge = Math.max(metadata.width, metadata.height);
            const shortEdge = Math.min(metadata.width, metadata.height);
            const edgeToCheck = shortEdgeSize ? shortEdge : longEdge;
            const targetValue = shortEdgeSize || targetSize;
            
            if (edgeToCheck < parseInt(targetValue)) {
              // Image needs upscaling - use mid-point progress
              onProgress(progressDuringProcessing, `Upscaling image ${i + 1}...`);
              
              const upscaleResult = await window.electronAPI.upscaleImage(filePath, licenseInfo, {
                targetSize: targetSize ? parseInt(targetSize) : undefined,
                shortEdgeSize: shortEdgeSize ? parseInt(shortEdgeSize) : undefined
              });
              if (upscaleResult.success) {
                processPath = upscaleResult.imagePath || filePath;
                upscaledCount++;
              } else {
                console.warn(`Failed to upscale ${filePath}:`, upscaleResult.error);
                // Continue with original image if upscaling fails
              }
            }
          }
        }
        
        // Show "processing" phase for small batches
        if (total <= 3) {
          const processingProgress = (currentItem - 1) / total * 50 + (1 / total * 30); // 30% through current image
          onProgress(processingProgress, `Processing image ${currentItem}/${total}...`);
        }
        
        // Process the image (original or upscaled)
        const result = await window.electronAPI.processImage(processPath, outputPath, {
          targetSize: targetSize ? parseInt(targetSize) : undefined,
          shortEdgeSize: shortEdgeSize ? parseInt(shortEdgeSize) : undefined,
          cropWidth: cropWidth ? parseInt(cropWidth) : undefined,
          cropHeight: cropHeight ? parseInt(cropHeight) : undefined,
          enableAIFill: enableAIFill || false,
          cropAlignment: settings.cropAlignment || 'center',
          useSystemPrompt: settings.useSystemPrompt !== undefined ? settings.useSystemPrompt : true,
          customPrompt: settings.customPrompt || '',
          format,
          quality: parseInt(quality),
          licenseInfo
        });
        
        if (result.success) {
          // Show "saving" phase for small batches
          if (total <= 3) {
            const savingProgress = (currentItem - 1) / total * 50 + (1 / total * 40); // 40% through current image  
            onProgress(savingProgress, `Saving image ${currentItem}/${total}...`);
          }
          
          processedCount++; // Increment after successful processing
          processedFiles.push({
            inputPath: filePath,
            outputPath,
            fileName,
            ...result
          });
        } else {
          console.error(`Failed to process ${filePath}:`, result.error);
          throw new Error(`Failed to process ${getBasename(filePath)}: ${result.error}`);
        }
      } // End of iterations loop
    } // End of files loop
    
    if (processedFiles.length === 0) {
      throw new Error('No files were successfully processed');
    }
    
    // Step 2: Upload to S3 (50-100% progress)
    for (let i = 0; i < processedFiles.length; i++) {
      const file = processedFiles[i];
      
      // Update progress for upload (50-100%)
      const uploadProgress = 50 + ((i + 1) / processedFiles.length) * 50;
      onProgress(uploadProgress, `Uploading to S3: ${i + 1}/${processedFiles.length}`);
      
      // Upload to S3
      const uploadResult = await window.electronAPI.uploadToS3(
        file.outputPath,
        bucketName,
        folderPath
      );
      
      if (uploadResult.success) {
        uploadedFiles.push({
          ...file,
          s3Url: uploadResult.url,
          s3Key: uploadResult.key
        });
      } else {
        console.error(`Failed to upload ${file.outputPath}:`, uploadResult.error);
        throw new Error(`Failed to upload ${file.fileName}: ${uploadResult.error}`);
      }
    }
    
    // Step 3: Generate CSV report
    if (uploadedFiles.length > 0) {
      const csvData = [
        ['name', 'S3 URL'], // Headers
        ...uploadedFiles.map(file => [
          file.fileName.replace(/\.[^/.]+$/, ''), // Remove extension
          file.s3Url
        ])
      ];
      
      const csvFileName = `upload_report_${new Date().toISOString().slice(0, 19).replace(/[:-]/g, '')}.csv`;
      const csvPath = `${outputDirectory}/${csvFileName}`;
      
      await window.electronAPI.writeCSV(csvPath, csvData);
      
      console.log(`CSV report saved to: ${csvPath}`);
    }
    
    // Final progress update
    onProgress(100, 'Complete!');
    
    return {
      processedFiles,
      uploadedFiles,
      csvPath: uploadedFiles.length > 0 ? `${outputDirectory}/upload_report_${new Date().toISOString().slice(0, 19).replace(/[:-]/g, '')}.csv` : null
    };
    
  } catch (error) {
    console.error('Error in uploadToS3:', error);
    throw error;
  }
};

export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

export const getImageDimensions = (width, height, targetSize) => {
  if (width > height) {
    const newWidth = targetSize;
    const newHeight = Math.round(height * (targetSize / width));
    return { width: newWidth, height: newHeight };
  } else {
    const newHeight = targetSize;
    const newWidth = Math.round(width * (targetSize / height));
    return { width: newWidth, height: newHeight };
  }
};